import React from 'react';
import { useTranslation } from 'react-i18next';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';

function SystemJurisdictionsPageView({
  rows,
  countryOptions,
  isLoading,
  isSaving,
  error,
  success,
  detailsRow,
  editingTargetId,
  isCreateOpen,
  deleteTargetId,
  form,
  formErrors,
  closeModals,
  openCreate,
  openEdit,
  openDetails,
  requestDelete,
  onFormChange,
  onCountriesChange,
  saveForm,
  confirmDelete,
}) {
  const { t } = useTranslation();
  return (
    <div>
      <FloatingAlerts
        info={isSaving ? t('jurisdictions.savingInfo') : ''}
        error={error}
        success={success}
      />
      <div className="app-page-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2>{t('jurisdictions.title')}</h2>
          <p>{t('jurisdictions.subtitle')}</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={openCreate} disabled={isLoading || isSaving}>
          {t('jurisdictions.addJurisdiction')}
        </button>
      </div>

      <div className="widget-content searchable-container list">
        <div className="card card-body">
          <LoadingContainer isLoading={isLoading} hasData={rows.length > 0}>
              <table className="table search-table align-middle text-nowrap mb-0">
                <thead className="header-item">
                  <tr>
                    <th>{t('jurisdictions.colName')}</th>
                    <th>{t('jurisdictions.colCountries')}</th>
                    <th style={{ width: '1%', whiteSpace: 'nowrap' }} className="text-center">{t('jurisdictions.colDefault')}</th>
                    <th style={{ width: '1%', whiteSpace: 'nowrap' }} className="text-end">{t('jurisdictions.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center text-muted">{t('jurisdictions.noJurisdictionsFound')}</td>
                    </tr>
                  ) : null}
                  {rows.map((row) => (
                    <tr key={row.id} className="app-clickable-row" onClick={() => openDetails(row.id)}>
                      <td>{row.name || '-'}</td>
                      <td>{row.countries.length > 0 ? row.countries.join(', ') : '-'}</td>
                      <td className="text-center">{row.byDefault ? t('common.yes') : t('common.no')}</td>
                      <td className="text-nowrap text-end">
                        <div className="d-flex align-items-center justify-content-end gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            title="Edit"
                            onClick={(event) => {
                              event.stopPropagation();
                              openEdit(row);
                            }}
                            disabled={isSaving}
                          >
                            <i className="fa fa-pen"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            title="Delete"
                            onClick={(event) => {
                              event.stopPropagation();
                              requestDelete(row.id);
                            }}
                            disabled={isSaving}
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
          </LoadingContainer>
        </div>
      </div>

      {detailsRow ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{t('jurisdictions.detailsTitle')}</h5>
                  <button type="button" className="btn-close" onClick={closeModals}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <strong>{t('jurisdictions.fieldName')}:</strong>
                    <div>{detailsRow.name || '-'}</div>
                  </div>
                  <div>
                    <strong>{t('jurisdictions.fieldCountries')}:</strong>
                    <div>{detailsRow.countries.length > 0 ? detailsRow.countries.join(', ') : '-'}</div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-default" onClick={closeModals}>{t('common.close')}</button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      ) : null}

      {isCreateOpen || editingTargetId ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editingTargetId ? t('jurisdictions.editTitle') : t('jurisdictions.addTitle')}</h5>
                  <button type="button" className="btn-close" onClick={closeModals}></button>
                </div>
                <form onSubmit={saveForm}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">{t('jurisdictions.nameLabel')}</label>
                      <input
                        className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                        value={form.name}
                        onChange={(event) => onFormChange('name', event.target.value)}
                        required
                      />
                      {formErrors.name ? <div className="invalid-feedback">{formErrors.name}</div> : null}
                    </div>

                    <div className="mb-3">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="jurisdiction-by-default"
                          checked={Boolean(form.byDefault)}
                          onChange={(event) => onFormChange('byDefault', event.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="jurisdiction-by-default">{t('jurisdictions.useByDefault')}</label>
                      </div>
                      {formErrors.byDefault ? <div className="text-danger small mt-1">{formErrors.byDefault}</div> : null}
                    </div>

                    <div>
                      <label className="form-label">{t('jurisdictions.countriesLabel')}</label>
                      <select
                        multiple
                        className={`form-select ${formErrors.countries ? 'is-invalid' : ''}`}
                        value={form.countries}
                        onChange={onCountriesChange}
                        size={8}
                        style={{ maxHeight: '220px' }}
                      >
                        {countryOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      <div className="form-text">{t('jurisdictions.countriesHint')}</div>
                      {formErrors.countries ? <div className="invalid-feedback d-block">{formErrors.countries}</div> : null}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-default" onClick={closeModals} disabled={isSaving}>{t('common.cancel')}</button>
                    <button type="submit" className="btn btn-primary" disabled={isSaving}>
                      {editingTargetId ? t('common.save') : t('common.create')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      ) : null}

      {deleteTargetId ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{t('jurisdictions.deleteTitle')}</h5>
                  <button type="button" className="btn-close" onClick={closeModals}></button>
                </div>
                <div className="modal-body">
                  {t('jurisdictions.deleteConfirm')}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-default" onClick={closeModals} disabled={isSaving}>{t('common.cancel')}</button>
                  <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={isSaving}>{t('common.delete')}</button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      ) : null}
    </div>
  );
}

export default SystemJurisdictionsPageView;
